import React, { useMemo, useState } from "react";
import {
  FileText,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  Wallet,
  ChevronLeft,
  RefreshCw,
  User,
  Mail,
  Hash,
  X,
  PlusCircle,
  Upload,
} from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  useUserInvoicesQuery,
  useAdminRecordAdvancePaymentMutation,
  useGetLedgerAccountsQuery,
} from "../api/billingApi";
import { formatCurrency, formatDate } from "../../../utils/formatters";
import { useAccess } from "../../../utils/useAccess";
import { PERMS } from "../../../utils/perms";

export default function UserInvoicesPage() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, refetch, isFetching } = useUserInvoicesQuery(
    { userId: userId as string },
    { skip: !userId }
  );

  const invoices = data?.invoices || [];
  const userData = data?.user;

  // Advance payment modal state
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [advAmount, setAdvAmount] = useState("");
  const [advMethod, setAdvMethod] = useState("bank_transfer");
  const [advReference, setAdvReference] = useState("");
  const [advBankId, setAdvBankId] = useState("");
  const [advDate, setAdvDate] = useState(new Date().toISOString().split("T")[0]);
  const [advProof, setAdvProof] = useState<File | null>(null);
  const [advNotes, setAdvNotes] = useState("");
  const [advError, setAdvError] = useState<string | null>(null);

  const [recordAdvance, { isLoading: isSubmitting }] = useAdminRecordAdvancePaymentMutation();
  const { data: ledgerAccounts = [] } = useGetLedgerAccountsQuery();

  const { can } = useAccess();
  const canPostAdvance =
    can.role("super admin") || can.perm(PERMS.billing.advancePost);

  const handleAdvanceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdvError(null);
    if (!advBankId) { setAdvError("Please select a bank account."); return; }
    if (!advReference.trim()) { setAdvError("Reference is required."); return; }

    const formData = new FormData();
    formData.append("amount", advAmount);
    formData.append("payment_method", advMethod);
    formData.append("reference", advReference);
    formData.append("bank_account_id", advBankId);
    formData.append("paid_at", advDate);
    formData.append("notes", advNotes);
    if (advProof) formData.append("proof_of_payment", advProof);

    try {
      const result = await recordAdvance({ userId: userId!, formData }).unwrap();
      setShowAdvanceModal(false);
      navigate(`/receipt/${result.receipt_number || result.reference}`);
    } catch (err: any) {
      if (err?.status === 409) {
        setAdvError("This reference has already been used.");
      } else if (err?.status === 422 && err?.data?.errors) {
        const first = Object.keys(err.data.errors)[0];
        setAdvError(err.data.errors[first][0]);
      } else {
        setAdvError(err?.data?.message || "Failed to record payment.");
      }
    }
  };

  const statusConfig: Record<
    string,
    { label: string; color: string; icon: any }
  > = {
    paid: {
      label: "Settled",
      color: "bg-emerald-50 text-emerald-700 border-emerald-100",
      icon: CheckCircle2,
    },
    partial: {
      label: "Partial",
      color: "bg-amber-50 text-amber-700 border-amber-100",
      icon: Clock,
    },
    unpaid: {
      label: "Outstanding",
      color: "bg-rose-50 text-rose-700 border-rose-100",
      icon: AlertCircle,
    },
    pending: {
      label: "Pending",
      color: "bg-amber-50 text-amber-700 border-amber-100",
      icon: Clock,
    },
    cancelled: {
      label: "Void",
      color: "bg-slate-100 text-slate-500 border-slate-200",
      icon: ShieldCheck,
    },
  };

  const stats = useMemo(() => {
    return invoices.reduce(
      (acc: any, inv: any) => ({
        totalBalance: acc.totalBalance + (inv.balance || 0),
        pendingCount:
          inv.status !== "paid" ? acc.pendingCount + 1 : acc.pendingCount,
      }),
      { totalBalance: 0, pendingCount: 0 }
    );
  }, [invoices]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex flex-col items-center justify-center p-6">
        <RefreshCw className="w-12 h-12 text-primary animate-spin mb-6" />
        <span className="text-[10px] font-black uppercase tracking-[0.5em] text-slate-400">
          Syncing Ledger...
        </span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-32">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-10 py-12">
        {/* Header Section */}
        <header className="flex flex-col xl:flex-row xl:items-center justify-between gap-10 mb-16">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-3 bg-white border border-slate-200 rounded-2xl shadow-sm text-slate-600 hover:text-primary transition-all"
              >
                <ChevronLeft size={20} />
              </button>
              <nav className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                Billing Registry
              </nav>
            </div>

            {/* User Profile Card */}
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-[2rem] bg-slate-900 flex items-center justify-center text-white shadow-2xl shadow-slate-200">
                <User size={32} strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-4xl lg:text-5xl font-[1000] text-slate-900 tracking-tight leading-none uppercase">
                  {userData?.name}
                  <span className="text-primary italic">.</span>
                </h1>
                <div className="flex flex-wrap gap-4 mt-3">
                  <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400">
                    <Mail size={14} /> {userData?.email}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-widest">
                    <Hash size={14} />{" "}
                    {userData?.membership_no || "No Member ID"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats + Action */}
          <div className="flex flex-col gap-4 w-full xl:w-auto">
            <div className="bg-white rounded-[2.5rem] p-8 border-2 border-slate-900 shadow-xl shadow-slate-100 flex-1 md:min-w-[280px]">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-2">
                <Wallet className="w-3 h-3" /> Total Outstanding
              </div>
              <div className="text-4xl font-[1000] text-slate-900 tracking-tighter">
                {formatCurrency(stats.totalBalance)}
              </div>
            </div>
            {canPostAdvance && (
              <button
                onClick={() => setShowAdvanceModal(true)}
                className="flex items-center justify-center gap-2 w-full py-4 px-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-[2rem] text-[11px] font-black uppercase tracking-widest transition-all shadow-lg"
              >
                <PlusCircle size={16} /> Credit Advance Payment
              </button>
            )}
          </div>
        </header>

        {/* Invoices List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-6 mb-6">
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">
              Transaction History ({invoices.length})
            </h2>
            <div className="h-[1px] flex-1 bg-slate-100 mx-6 hidden md:block" />
            <button
              onClick={() => refetch()}
              className="text-[10px] font-black text-primary uppercase tracking-widest hover:underline flex items-center gap-2"
            >
              <RefreshCw
                size={12}
                className={isFetching ? "animate-spin" : ""}
              />{" "}
              Refresh
            </button>
          </div>

          {!invoices.length ? (
            <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-[3rem] p-32 text-center">
              <FileText className="w-16 h-16 mx-auto text-slate-200 mb-6" />
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-400">
                Zero records found
              </p>
            </div>
          ) : (
            invoices.map((inv: any) => {
              const status = statusConfig[inv.status] || statusConfig.cancelled;
              const StatusIcon = status.icon;
              return (
                <div
                  key={inv.id}
                  className="group bg-white border border-slate-100 rounded-[2.5rem] p-2 hover:border-slate-900 transition-all duration-500 shadow-sm hover:shadow-2xl"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-8 p-6 lg:p-8">
                    <div className="flex items-center gap-6 lg:w-[30%]">
                      <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-slate-900 group-hover:text-white transition-all">
                        <FileText size={30} strokeWidth={2.5} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-[900] text-slate-900 uppercase tracking-tight text-xl mb-2 truncate">
                          {inv.invoice_number}
                        </h3>
                        <div className="flex items-center gap-2">
                          <div
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[10px] font-black uppercase tracking-tighter ${status.color}`}
                          >
                            <StatusIcon size={12} /> {status.label}
                          </div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase">
                            {formatDate(inv.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 lg:flex items-center gap-8 lg:flex-1 lg:border-x border-slate-100 lg:px-10">
                      <div className="space-y-1">
                        <p className="text-[10px] font-black text-slate-300 uppercase">
                          Payable
                        </p>
                        <p className="font-bold text-slate-700 text-base">
                          {formatCurrency(inv.total_amount)}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-black text-slate-300 uppercase">
                          Paid
                        </p>
                        <p className="font-bold text-emerald-600 text-base">
                          {formatCurrency(inv.amount_paid)}
                        </p>
                      </div>
                      <div className="col-span-2 lg:col-span-1 space-y-1 lg:ml-auto">
                        <p className="text-[10px] font-black text-primary uppercase">
                          Balance
                        </p>
                        <p className="font-[1000] text-slate-900 text-2xl tracking-tighter">
                          {formatCurrency(inv.balance)}
                        </p>
                      </div>
                    </div>

                    <div className="shrink-0 lg:w-48">
                      <Link
                        to={`/admin/users/${userId}/invoice/${inv.id}`}
                        className="flex items-center justify-center gap-3 w-full py-4 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-primary transition-all shadow-lg group/btn"
                      >
                        Details{" "}
                        <ArrowUpRight
                          size={16}
                          className="group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-all"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Advance Payment Modal */}
      {showAdvanceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-8 border-b border-slate-100">
              <div>
                <h2 className="text-xl font-[1000] text-slate-900 uppercase tracking-tight">
                  Credit Advance Payment
                </h2>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                  {userData?.name} — no invoice required
                </p>
              </div>
              <button
                onClick={() => setShowAdvanceModal(false)}
                className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 transition-all"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAdvanceSubmit} className="p-8 space-y-5 overflow-y-auto flex-1">
              {/* Amount */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Amount (₦)
                </label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  required
                  value={advAmount}
                  onChange={(e) => setAdvAmount(e.target.value)}
                  placeholder="e.g. 25000"
                  className="w-full border-2 border-slate-200 focus:border-slate-900 rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all"
                />
              </div>

              {/* Payment Method */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Payment Method
                </label>
                <select
                  value={advMethod}
                  onChange={(e) => setAdvMethod(e.target.value)}
                  className="w-full border-2 border-slate-200 focus:border-slate-900 rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all bg-white"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="pos">POS</option>
                </select>
              </div>

              {/* Bank Account */}
              <div>
                <label className="text-[9px] font-black text-slate-400 uppercase mb-2 block tracking-widest">
                  Bank Account Paid Into
                </label>
                <select
                  required
                  className={`w-full bg-slate-50 border p-4 rounded-xl font-bold text-sm outline-none focus:border-orange-500 transition-all appearance-none ${
                    advError && !advBankId ? "border-rose-300" : "border-slate-100"
                  }`}
                  value={advBankId}
                  onChange={(e) => { setAdvBankId(e.target.value); setAdvError(null); }}
                >
                  <option value="">— Select account —</option>
                  {ledgerAccounts.map((acc: any) => (
                    <option key={acc.id} value={acc.id}>
                      {[acc.account_name ?? acc.name, acc.code, acc.account_number]
                        .filter(Boolean)
                        .join(" — ")}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reference */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Transaction Reference
                </label>
                <input
                  type="text"
                  required
                  value={advReference}
                  onChange={(e) => setAdvReference(e.target.value)}
                  placeholder="e.g. TRF/2026/00123"
                  className="w-full border-2 border-slate-200 focus:border-slate-900 rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all"
                />
              </div>

              {/* Payment Date */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Payment Date
                </label>
                <input
                  type="date"
                  required
                  value={advDate}
                  onChange={(e) => setAdvDate(e.target.value)}
                  className="w-full border-2 border-slate-200 focus:border-slate-900 rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all"
                />
              </div>

              {/* Proof of Payment */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Proof of Payment (optional)
                </label>
                <label className="flex items-center gap-3 border-2 border-dashed border-slate-200 hover:border-slate-400 rounded-2xl px-4 py-3 cursor-pointer transition-all">
                  <Upload size={16} className="text-slate-400 shrink-0" />
                  <span className="text-sm font-bold text-slate-400 truncate">
                    {advProof ? advProof.name : "Click to upload JPG, PNG or PDF (max 2MB)"}
                  </span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={(e) => setAdvProof(e.target.files?.[0] ?? null)}
                  />
                </label>
              </div>

              {/* Notes */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Notes (optional)
                </label>
                <textarea
                  rows={2}
                  value={advNotes}
                  onChange={(e) => setAdvNotes(e.target.value)}
                  placeholder="Any additional notes…"
                  className="w-full border-2 border-slate-200 focus:border-slate-900 rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all resize-none"
                />
              </div>

              {advError && (
                <p className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">
                  {advError}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  <PlusCircle size={14} />
                )}
                {isSubmitting ? "Recording…" : "Record Advance Payment"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
